import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {PrintTemplate} from "crqlar-react-common/dist/models/template/printTemplate.model";
import {PrintTemplateService} from "crqlar-react-common/dist/services/template/printTemplate.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const PrintTemplatesList = () => {
    useTrackScreenVisit('system print templates');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<PrintTemplate>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: template => template.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: template => template.type,
            cell: ({row}) => (
                <Box>{row.original.type ? t(`Print template type ${row.original.type}`) : t('(Not set)')}</Box>
            ),
            size: 260,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: template => template.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: template => template.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-print-templates',
        columns,
        getRowId: template => template.id!,
        onRowClick: template => navigate(`/system/printTemplates/${template.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await PrintTemplateService.getAllTemplates({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Print templates')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create print template')}
                            onClick={() => navigate('/system/printTemplates/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default PrintTemplatesList;
